import {newSpecPage} from '@stencil/core/testing';
import {Dialog} from '../dialog';
import {DialogTrigger} from '../../JoyDialogTrigger/dialog-trigger';

function fireEscapeEvent() {
    const event = new KeyboardEvent('keydown', {code: 'Escape'});
    document.dispatchEvent(event);
}

it('should show dialog only when we ask for it with a trigger', async () => {
    const page = await newSpecPage({
        components: [Dialog, DialogTrigger],
        html: `
      <joy-dialog-trigger dialog="myDialog">Trigger</joy-dialog-trigger>          
      <joy-dialog id="myDialog"></joy-dialog>
    `,
    });

    const dialog = page.body.querySelector('joy-dialog');
    const trigger = page.body.querySelector('joy-dialog-trigger');

    expect(dialog?.open).toBe(false);
    trigger?.click();
    await page.waitForChanges();

    expect(dialog?.open).toBe(true);
});

it('should close dialog and fire custom event for each scenarios: icon, cta, backdrop, keyboard escape and closeDialog method call', async () => {
    const page = await newSpecPage({
        components: [Dialog],
        html: `
        <joy-dialog id="myDialog" open confirm-text="Confirm" cancel-text="Cancel">
            <p slot="dialog-header">I am the dialog header</p>
            <p slot="dialog-subheader">I am the dialog subheader</p>
        </joy-dialog>`,
    });

    const spyEmitCancel = jest.spyOn(page.rootInstance.joyCancelDialog, 'emit');
    expect(page.rootInstance.open).toBe(true);

    // Click on cancel button
    const cancel: HTMLElement = page.root?.shadowRoot?.querySelector('.joy-dialog--cancel')!;
    cancel.click();
    expect(page.rootInstance.open).toBe(false);
    expect(spyEmitCancel).toHaveBeenCalledTimes(1);

    // Reset
    page.rootInstance.open = true;

    // Click on icon button
    const icon: HTMLElement = page.root?.shadowRoot?.querySelector('.joy-dialog--close')!;
    icon.click();
    expect(page.rootInstance.open).toBe(false);
    expect(spyEmitCancel).toHaveBeenCalledTimes(2);

    // Reset
    page.rootInstance.open = true;

    // Click on backdrop
    const backdrop: HTMLElement = page.root?.shadowRoot?.querySelector('.joy-dialog__overlay')!;
    backdrop.click();
    expect(page.rootInstance.open).toBe(false);
    expect(spyEmitCancel).toHaveBeenCalledTimes(3);

    // Reset
    page.rootInstance.open = true;

    // Trigger closeDialog method
    page.rootInstance.closeDialog();
    expect(page.rootInstance.open).toBe(false);
    expect(spyEmitCancel).toHaveBeenCalledTimes(4);

    // Reset
    page.rootInstance.open = true;

    // Keyboard Escape press
    fireEscapeEvent();
    expect(page.rootInstance.open).toBe(false);
    expect(spyEmitCancel).toHaveBeenCalledTimes(5);
});

it('should not close dialog by clicking on confirm CTA', async () => {
    const page = await newSpecPage({
        components: [Dialog],
        html: `
        <joy-dialog id="myDialog" open confirm-text="Confirm" cancel-text="Cancel">
          <p slot="dialog-header">I am the dialog header</p>
          <p slot="dialog-subheader">I am the dialog subheader</p>
      </joy-dialog>`,
    });

    const confirm: HTMLElement = page.root?.shadowRoot?.querySelector('.joy-dialog--confirm')!;
    confirm.click();
    expect(page.rootInstance.open).toBe(true);
});

it('should not be able to close dialog by clicking on overlay or close icon when using confirm pattern-confirm', async () => {
    const page = await newSpecPage({
        components: [Dialog],
        html: `
        <joy-dialog id="myDialog" open pattern-confirm confirm-text="Confirm" cancel-text="Cancel">
          <p slot="dialog-header">I am the dialog header</p>
          <p slot="dialog-subheader">I am the dialog subheader</p>
      </joy-dialog>`,
    });

    // Click on backdrop
    const backdrop: HTMLElement = page.root?.shadowRoot?.querySelector('.joy-dialog__overlay')!;
    backdrop.click();
    expect(page.rootInstance.open).toBe(true);

    // Click on icon (but there is no icon)
    const closeIcon = page.root?.shadowRoot?.querySelector('.joy-dialog--close');
    expect(closeIcon).toBeNull();

    // Press escape
    fireEscapeEvent();
    expect(page.rootInstance.open).toBe(true);
});

it('should not fire a close event when calling public closeDialog with specific param value', async () => {
    const page = await newSpecPage({
        components: [Dialog],
        html: `
          <joy-dialog id="myDialog" open confirm-text="Confirm" cancel-text="Cancel">
            <p slot="dialog-header">I am the dialog header</p>
            <p slot="dialog-subheader">I am the dialog subheader</p>
        </joy-dialog>`,
    });

    const spyEmitCancel = jest.spyOn(page.rootInstance.joyCancelDialog, 'emit');
    page.rootInstance.closeDialog({fireEscapeEvent: false});
    expect(spyEmitCancel).toHaveBeenCalledTimes(0);
});

it('should fire a confirm event when clicking on dialog-confirm slot', async () => {
    const page = await newSpecPage({
        components: [Dialog],
        html: `
        <joy-dialog id="myDialog" open>
            <p slot="dialog-header">I am the dialog header</p>
            <joy-button slot="dialog-confirm">Confirm</joy-button>
        </joy-dialog>`,
    });

    const spyConfirmEvent = jest.spyOn(page.rootInstance.joyConfirmDialog, 'emit');
    const confirm: HTMLElement = page.body.querySelector('[slot="dialog-confirm"]')!;
    confirm.click();

    expect(spyConfirmEvent).toHaveBeenCalledTimes(1);
});
